import { useBookingFlow } from "../../session/BookingFlowProvider";
import { ChoiceCard, GuaranteeBadge, Notice, OptionRow, Panel } from "../../components/ui";
import { hasMovedPast, reasonsForStep } from "../FlowMachine";

export function CategoryStep() {
  const { projection, bootstrap, send, pending, visited } = useBookingFlow();
  const autoNote = projection.availability?.autoSelectionNote;
  const movedPast = hasMovedPast(bootstrap, projection, "category", visited);

  return (
    <Panel title="Welches Zimmer darf es sein?">
      {autoNote ? (
        <Notice tone="info" title="Für Sie vorausgewählt">
          <p>{autoNote}</p>
        </Notice>
      ) : null}

      {projection.rooms.map((room, index) => {
        const reasons = reasonsForStep(projection, "category", room.roomId);
        return (
          <fieldset key={room.roomId} className="room-block room-block--flush">
            {projection.rooms.length > 1 ? (
              <legend>
                Zimmer {index + 1} · {room.partyLabel}
              </legend>
            ) : null}
            <div className="card-grid">
              {bootstrap.categories.map((category) => {
                const availability = projection.availability?.categories.find((c) => c.code === category.code);
                // Whether this room's party fits is the server's answer, per room — two rooms in
                // one booking can have different parties.
                const fit = room.categoryOptions.find((option) => option.code === category.code);
                const soldOut = availability?.status === "unavailable";
                const disabled = soldOut || fit?.selectable === false;
                return (
                  <ChoiceCard
                    key={category.code}
                    title={category.displayName}
                    description={category.description}
                    selected={room.categoryCode === category.code}
                    disabled={disabled || pending}
                    onSelect={() =>
                      void send({ type: "set_room_category", roomId: room.roomId, categoryCode: category.code })
                    }
                    media={category.imageUrl}
                    meta={
                      availability?.fromPrice ? (
                        <span className="choice-price">
                          <span className="choice-price-label">ab</span>
                          {availability.fromPrice.formatted}
                        </span>
                      ) : null
                    }
                  >
                    <span className="choice-meta">
                      Bis {category.maxOccupancy} {category.maxOccupancy === 1 ? "Person" : "Personen"}
                      {availability?.fromPrice ? " · pro Nacht" : ""}
                    </span>
                    {category.guaranteedAttributes.length > 0 ? (
                      <span className="attribute-list">
                        {category.guaranteedAttributes.map((attribute) => (
                          <span key={attribute.code} className="attribute">
                            {attribute.displayName}
                            <GuaranteeBadge level={attribute.guaranteeLevel} />
                          </span>
                        ))}
                      </span>
                    ) : null}
                    {availability?.remainingHint ? (
                      <span className="badge badge--limited">{availability.remainingHint}</span>
                    ) : null}
                    {/* Said where the choice is made, not as an error after the click. */}
                    {fit && !fit.selectable ? <span className="choice-blocked">{fit.reason}</span> : null}
                  </ChoiceCard>
                );
              })}
            </div>
            {(movedPast ? reasons : reasons.filter((r) => r.code !== "CATEGORY_REQUIRED")).map((reason) => (
              <p key={`${reason.code}-${reason.roomId}`} className="field-error" role="alert">
                {reason.message}
              </p>
            ))}
          </fieldset>
        );
      })}
    </Panel>
  );
}

export function RateStep() {
  const { projection, bootstrap, send, pending, visited } = useBookingFlow();
  const movedPast = hasMovedPast(bootstrap, projection, "rate", visited);

  return (
    <Panel title="Verpflegung und Konditionen" description="Nur Raten, die für Ihren Zeitraum vollständig bepreist sind.">
      {projection.rooms.map((room, index) => {
        const reasons = reasonsForStep(projection, "rate", room.roomId);
        return (
          <fieldset key={room.roomId} className="room-block">
            {projection.rooms.length > 1 ? <legend>Zimmer {index + 1}</legend> : null}
            <div className="card-grid">
              {bootstrap.rates.map((rate) => (
                <ChoiceCard
                  key={rate.code}
                  title={rate.displayName}
                  description={rate.description}
                  selected={room.rateProductCode === rate.code}
                  disabled={pending}
                  onSelect={() =>
                    void send({ type: "set_room_rate", roomId: room.roomId, rateProductCode: rate.code })
                  }
                  meta={<span className="badge badge--meal">{rate.mealPlanLabel}</span>}
                >
                  {rate.includedBenefits.length > 0 ? (
                    <span className="choice-meta">Enthalten: {rate.includedBenefits.join(", ")}</span>
                  ) : null}
                  {rate.cancellationSummary ? (
                    <span className="choice-meta">{rate.cancellationSummary}</span>
                  ) : null}
                  {rate.paymentSummary ? <span className="choice-meta">{rate.paymentSummary}</span> : null}
                </ChoiceCard>
              ))}
            </div>
            {(movedPast ? reasons : reasons.filter((r) => r.code !== "RATE_REQUIRED")).map((reason) => (
              <p key={`${reason.code}-${reason.roomId}`} className="field-error" role="alert">
                {reason.message}
              </p>
            ))}
          </fieldset>
        );
      })}
    </Panel>
  );
}

export function PreferencesStep() {
  const { projection, bootstrap, send, pending } = useBookingFlow();
  const hasRequirements = bootstrap.requirements.length > 0;

  return (
    <Panel
      title="Wünsche und Anforderungen"
      description="Anforderungen werden vor der Buchung geprüft. Wünsche merken wir vor, ohne sie zuzusagen."
    >
      {projection.rooms.map((room, index) => (
        <fieldset key={room.roomId} className="room-block">
          {projection.rooms.length > 1 ? <legend>Zimmer {index + 1}</legend> : null}

          {hasRequirements ? (
            <>
              <h3 className="subhead">Feste Anforderungen</h3>
              {bootstrap.requirements.map((requirement) => (
                <OptionRow
                  key={requirement.code}
                  option={requirement}
                  checked={room.requirements.includes(requirement.code)}
                  disabled={pending}
                  onToggle={(selected) =>
                    void send({
                      type: "set_room_requirement",
                      roomId: room.roomId,
                      optionCode: requirement.code,
                      selected,
                    })
                  }
                />
              ))}
            </>
          ) : null}

          {bootstrap.preferences.length > 0 ? (
            <>
              <h3 className="subhead">Unverbindliche Wünsche</h3>
              {bootstrap.preferences.map((preference) => (
                <OptionRow
                  key={preference.code}
                  option={preference}
                  checked={room.preferences.includes(preference.code)}
                  disabled={pending}
                  onToggle={(selected) =>
                    void send({
                      type: "set_room_preference",
                      roomId: room.roomId,
                      optionCode: preference.code,
                      selected,
                    })
                  }
                />
              ))}
            </>
          ) : null}

          {index === 0 && bootstrap.preferences.length > 0 ? (
            <Notice tone="info" title="Wünsche sind keine Zusage.">
              <p>Wir geben sie an die Rezeption weiter und richten sie ein, wenn es geht.</p>
            </Notice>
          ) : null}
        </fieldset>
      ))}
    </Panel>
  );
}

export function ExtrasStep() {
  const { projection, bootstrap, send, pending } = useBookingFlow();
  const selectedRateCodes = new Set(projection.rooms.flatMap((room) => room.rateProductCode ?? []));
  const visibleAddons = bootstrap.addons.filter((option) => {
    const selected = projection.addons.some((addon) => addon.code === option.code);
    return selected || !option.conflictsWith.some((code) => selectedRateCodes.has(code));
  });

  return (
    <Panel title="Extras" description="Alles hier ist kostenpflichtig, sofern nicht anders ausgewiesen.">
      {visibleAddons.map((option) => {
        const selected = projection.addons.find((a) => a.code === option.code);
        return (
          <OptionRow
            key={option.code}
            option={option}
            checked={Boolean(selected)}
            disabled={pending}
            onToggle={(next) => {
              if (next) {
                void send({
                  type: "add_addon",
                  code: option.code,
                  roomId: option.scope === "room" ? (projection.rooms[0]?.roomId ?? null) : null,
                  quantity: 1,
                  fromDate: null,
                  toDate: null,
                });
              } else if (selected) {
                void send({ type: "remove_addon", addonId: selected.addonId });
              }
            }}
          />
        );
      })}
    </Panel>
  );
}
